// Remove the service import
// import { getVoiceRoomGoalsService } from '@/services/VoiceRoomService';

// Add a simple in-memory store for speaking timeouts within the action scope
// let speakingTimeouts = {};
// Reduce timeout for faster response when user stops speaking
// const SPEAKING_TIMEOUT_MS = 300; // Reduced from 1000ms 

// Adjust threshold based on observed silent levels
const SPEAKING_THRESHOLD = 40; // Increased threshold

import { getGifts } from '@/services/VideoRoomService';
import { showNotify, throwDefaultError } from '@/helpers/helpers';

export default {
  // Şimdilik action'ları component'te tuttuğumuz için boş olabilir
  // veya mevcut state ayarlama mantığını buraya taşıyabiliriz
  setRoomInfo({ commit }, roomInfo) {
    commit('SET_ROOM_INFO', roomInfo);
    if (!roomInfo) {
        commit('CLEAR_PENDING_REQUESTS');
        commit('SET_SPEAKERS', []);
        commit('SET_AUDIENCE', []);
        commit('SET_MAIN_HOST', null);
        commit('SET_STREAM_TITLE', '');
        commit('SET_GOALS', []);
        commit('SET_IS_HOST', false);
        commit('SET_IS_MUTED', false);
    }
  },
  setIsHost({ commit }, isHost) {
    commit('SET_IS_HOST', isHost);
  },
  setIsMuted({ commit }, isMuted) {
    commit('SET_IS_MUTED', isMuted);
  },
  // İleride isteği kabul/reddetme action'ları buraya eklenebilir
  // approveRequest({ commit, dispatch }, userId) { ... }
  // rejectRequest({ commit, dispatch }, userId) { ... }

  // Speaker Actions
  setSpeakers({ commit }, speakers) {
    commit('SET_SPEAKERS', speakers);
  },
  addSpeaker({ commit }, speaker) {
    commit('ADD_SPEAKER', speaker);
  },
  removeSpeaker({ commit }, speakerId) {
    commit('REMOVE_SPEAKER', speakerId);
  },
  updateSpeaker({ commit }, speakerUpdate) {
    commit('UPDATE_SPEAKER', speakerUpdate);
  },

  // Audience Actions
  setAudience({ commit }, audience) {
    commit('SET_AUDIENCE', audience);
  },
  addAudienceMember({ commit }, member) {
    commit('ADD_AUDIENCE_MEMBER', member);
  },
  removeAudienceMember({ commit }, memberId) {
    commit('REMOVE_AUDIENCE_MEMBER', memberId);
  },

  // Speaker Request Handling Actions
  handleSpeakerRequestApproved({ commit }, { userId, speakerData }) {
    commit('REMOVE_SPEAKER_REQUEST', userId);
    // speakerData should contain the necessary info like id, name, avatar etc.
    if (speakerData) {
      commit('ADD_SPEAKER', speakerData);
    }
  },
  handleSpeakerRequestRejected({ commit }, userId) {
    commit('REMOVE_SPEAKER_REQUEST', userId);
  },

  // Renamed and simplified: This action now just commits data passed from component
  setRoomDetails({ commit }, details) {
    if (details && details.streamTitle && details.goals) {
      commit('SET_STREAM_TITLE', details.streamTitle);
      commit('SET_GOALS', details.goals);
    } else {
      console.warn('[Store Action - setRoomDetails] Invalid details received:', details);
      // Optionally commit default/empty values if needed
      commit('SET_STREAM_TITLE', 'Başlık Yok')
      commit('SET_GOALS', []);
    }
  },

  // Action to update speaking status based on volume indicators
  updateSpeakingStatus({ commit, state }, volumes) {
    // Keep track of users processed in this batch to avoid conflicts
    const processedUsers = new Set();
    // Keep track of users who are currently speaking above threshold
    const usersCurrentlyAboveThreshold = new Set();

    volumes.forEach(({ uid, level }) => {
      const userId = uid; 
      processedUsers.add(userId);

      if (level >= SPEAKING_THRESHOLD) {
        usersCurrentlyAboveThreshold.add(userId);
        // Commit immediately if speaking
        // Check if state needs update to avoid unnecessary commits
        const currentUser = state.mainHost?.id === userId ? state.mainHost : state.speakers.find(s => s.id === userId);
        if (currentUser && !currentUser.isSpeaking) {
             commit('SET_USER_SPEAKING', { userId, isSpeaking: true });
        }
      } 
      // No need for an else block here, we handle users dropping below threshold later
    });

    // After processing all volumes, check which users *were* speaking but are no longer above threshold
    const usersToSilence = [];
    if (state.mainHost && state.mainHost.isSpeaking && !usersCurrentlyAboveThreshold.has(state.mainHost.id)) {
        usersToSilence.push(state.mainHost.id);
    }
    state.speakers.forEach(speaker => {
        if (speaker.isSpeaking && !usersCurrentlyAboveThreshold.has(speaker.id)) {
            usersToSilence.push(speaker.id);
        }
    });

    // Commit false for users who dropped below threshold
    usersToSilence.forEach(userId => {
        commit('SET_USER_SPEAKING', { userId, isSpeaking: false });
    });

    // Note: This logic assumes that Agora sends volume level 0 or very low
    // when a user becomes completely silent or mutes locally.
    // If Agora stops sending events for silent users, this might need adjustment.
  },

  // Action to handle user leaving (could be speaker or audience)
  processUserLeft({ commit, state }, userId) {
    if (!userId) {
      console.warn('[Store Action - processUserLeft] Received undefined or null userId. Skipping.');
      return;
    }
    
    if (state.speakers.some(s => s.id === userId)) {
        commit('REMOVE_SPEAKER', userId);
        
    } else if (state.audience.some(a => a.id === userId)) {
        commit('REMOVE_AUDIENCE_MEMBER', userId);
        
    }
    // Also remove from pending requests if they were there
    commit('REMOVE_SPEAKER_REQUEST', userId);
    // Ensure speaking status is set to false when user leaves
    commit('SET_USER_SPEAKING', { userId, isSpeaking: false });
  },

  // --- Updated Action ---
  processIncomingGift({ commit, state }, giftData) {
    
    // Check if goalProgressData exists in the event data
    if (!giftData || !giftData.goalProgressData || !Array.isArray(giftData.goalProgressData) || giftData.goalProgressData.length === 0) {
      return;
    }
    
    if (!state.goals || state.goals.length === 0) {
      
      return;
    }

    // Iterate through the progress data received from the event
    giftData.goalProgressData.forEach(progressUpdate => {
      // Find the corresponding goal in the state (match by goalType primarily)
      // If you have multiple goals of the same type, you might need a more specific identifier
      const goalIndex = state.goals.findIndex(g => g.goalType === progressUpdate.goalType);

      if (goalIndex !== -1) {
        // Commit the mutation with the values directly from the event
        commit('UPDATE_GOAL_PROGRESS', {
          goalIndex: goalIndex,
          newCurrentValue: progressUpdate.currentValue,
          newAchievedStatus: progressUpdate.achieved
        });
        
      } else {
        console.warn(`[Store Action - processIncomingGift] No matching goal found in state for goalType: ${progressUpdate.goalType} from event data.`);
      }
    });
  },
  // --- End Updated Action ---

  // Action to fetch available gifts and commit mutation
  fetchAvailableGifts: async ({ commit }) => {
    // Hediye satışı kaldırıldı — hediye listesi artık çekilmiyor
    commit('SET_AVAILABLE_GIFTS', [])
    //
    // try {
    //   const gifts = await getGifts();
    //
    //   commit('SET_AVAILABLE_GIFTS', gifts);
    //
    // } catch (error) {
    //   console.error('[ACTION] fetchAvailableGifts - Error fetching gifts:', error); // Keep error log
    //   throwDefaultError(null, error);
    //   commit('SET_AVAILABLE_GIFTS', []);
    // }
  },
}